import { useState, FormEvent, useEffect } from "react";
import { API_ENDPOINTS } from "../../constants/constants";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState("");
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenChecking, setTokenChecking] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromUrl = searchParams.get("token");

    if (!tokenFromUrl) {
      setError(
        "Token manquant. Veuillez utiliser le lien fourni dans l'email."
      );
      setTokenChecking(false);
      return;
    }

    console.log("Token reçu:", tokenFromUrl);
    setToken(tokenFromUrl);

    // Le backend ne semble pas offrir d'endpoint pour vérifier la validité du token
    // On passe directement au formulaire et la validation sera faite lors de la soumission
    setTokenValid(true);
    setTokenChecking(false);
  }, [location]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    setError("");

    try {
      console.log(
        "Envoi de la requête de réinitialisation avec le token:",
        token
      );

      const response = await fetch(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          resetToken: token,
          newPassword: password,
        }),
      });

      console.log("Statut de la réponse de réinitialisation:", response.status);

      // Lire la réponse JSON même en cas d'erreur
      const data = await response.json();
      console.log("Réponse du serveur:", data);

      if (!response.ok) {
        let errorMessage = "Erreur lors de la réinitialisation du mot de passe";

        // Utiliser le message d'erreur du serveur s'il existe
        if (data && data.message) {
          errorMessage = data.message;
        }

        throw new Error(errorMessage);
      }

      // Success
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000); // Redirect to login after 3 seconds
    } catch (err) {
      console.error("Erreur lors de la réinitialisation:", err);
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  // Show loading during token check
  if (tokenChecking) {
    return (
      <div className="min-h-screen w-full bg-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="/images/login.jpeg"
            alt="Fond avec clavier et mains"
            className="w-full h-full object-cover contrast-125 brightness-20"
          />
        </div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md bg-white/20 backdrop-blur-md p-8 text-center">
            <div className="mb-6">
              <h1 className="text-white text-3xl font-bold tracking-wider">
                <span>Guide</span>
                <span className="font-light">pro</span>
              </h1>
            </div>
            <p className="text-white">
              Vérification du lien de réinitialisation...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Token invalid
  if (!tokenValid && !tokenChecking) {
    return (
      <div className="min-h-screen w-full bg-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="/images/login.jpeg"
            alt="Fond avec clavier et mains"
            className="w-full h-full object-cover contrast-125 brightness-20"
          />
        </div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md bg-white/20 backdrop-blur-md p-8">
            <div className="mb-6">
              <h1 className="text-white text-3xl font-bold tracking-wider text-center">
                <span>Guide</span>
                <span className="font-light">pro</span>
              </h1>
            </div>
            <div className="bg-red-500/80 text-white p-4 mb-4">
              <p>{error || "Lien de réinitialisation invalide ou expiré"}</p>
              <p className="mt-2">
                Veuillez demander un nouveau lien de réinitialisation.
              </p>
            </div>
            <div className="text-center mt-4">
              <Link
                to="/forgot-password"
                className="text-white hover:text-green-300"
              >
                Demander un nouveau lien
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-800 relative overflow-hidden">
      {/* Image de fond (clavier et mains) - plus sombre et nette */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="/images/login.jpeg"
          alt="Fond avec clavier et mains"
          className="w-full h-full object-cover contrast-125 brightness-20"
        />
      </div>

      {/* Overlay semi-transparent plus foncé */}
      <div className="absolute inset-0 bg-black/50"></div>

      {/* Contenu principal avec formulaire décalé à gauche sur desktop, centré sur mobile */}
      <div className="relative z-10 flex flex-col items-center sm:items-start justify-center min-h-screen px-4 sm:px-8 md:px-16 md:pl-32">
        {/* Formulaire de réinitialisation de mot de passe */}
        <div className="w-full max-w-xs sm:max-w-sm md:max-w-md bg-white/20 backdrop-blur-md p-4 sm:p-6 md:p-8">
          {/* Logo GuidePro */}
          <div className="mb-4 sm:mb-6">
            <h1 className="text-white text-3xl sm:text-4xl font-bold tracking-wider text-center">
              <span>Guide</span>
              <span className="font-light">pro</span>
            </h1>
          </div>
          <h2 className="text-white text-center text-sm sm:text-base mb-4 sm:mb-6">
            Réinitialisation du mot de passe
          </h2>

          {error && (
            <div className="bg-red-500/80 text-white p-3 mb-4 text-sm">
              {error}
            </div>
          )}

          {success ? (
            <div className="bg-green-500/80 text-white p-3 mb-4 text-sm">
              <p>Votre mot de passe a été réinitialisé avec succès.</p>
              <p className="mt-2">
                Vous allez être redirigé vers la page de connexion...
              </p>
            </div>
          ) : (
            <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
              {/* Champ Mot de passe */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-white text-xs sm:text-sm mb-1"
                >
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/30 border border-white/50 text-white placeholder-white/70 text-sm pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                  >
                    {showPassword ? (
                      <FaEyeSlash size={18} />
                    ) : (
                      <FaEye size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirmation du mot de passe */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-white text-xs sm:text-sm mb-1"
                >
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/30 border border-white/50 text-white placeholder-white/70 text-sm pr-10"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={
                      showConfirmPassword
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash size={18} />
                    ) : (
                      <FaEye size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* Bouton d'envoi */}
              <div className="text-center mt-6">
                <button
                  type="submit"
                  className="bg-[#26853d] hover:bg-green-700 text-white font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-xs uppercase text-xs sm:text-sm"
                  disabled={loading}
                >
                  {loading
                    ? "Réinitialisation en cours..."
                    : "Réinitialiser le mot de passe"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Espace fixe de 2cm (environ 75px) sur mobile uniquement, caché sur desktop */}
        <div className="h-[1cm] block sm:hidden"></div>

        {/* Logo ministère en bas sur mobile, en bas à droite sur desktop */}
        <div className="w-full flex justify-center sm:justify-end sm:absolute sm:bottom-4 sm:right-4">
          <div className="text-white text-center">
            {/* Petite image/logo au-dessus du texte */}
            <div className="mb-1 sm:mb-3 flex justify-center">
              <img
                src="/images/armoirie-login.png"
                alt="Logo ministère"
                className="w-[50px] h-auto "
              />
            </div>
            <div className="text-xxs sm:text-xs leading-tight">
              MINISTÈRE DE LA FONCTION
              <br />
              PUBLIQUE ET DE LA MODERNISATION
              <br />
              DE L'ADMINISTRATION
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
